import type {TranslateLogger} from './logger';
import type {CodeMode} from './utils/config';

import {mkdirSync, writeFileSync} from 'node:fs';
import {dirname} from 'node:path';

import {TranslateError} from './utils';

/**
 * Version of the run report schema. Bump it on any breaking change of the
 * report shape, so consumers (analytics pipelines, dashboards) can detect
 * incompatible reports instead of silently misreading them.
 */
export const TRANSLATE_REPORT_SCHEMA_VERSION = 1;

export type TranslateReportStatus = 'success' | 'partial' | 'failed';

export type TranslateReportError = {
    /** Target language the error occurred for, when known. */
    target?: string;
    /** Input-relative file path the error relates to, when known. */
    path?: string;
    /** Stable error code (TranslateError code) or UNKNOWN. */
    code: string;
    message: string;
};

export type TranslateReportJudge = {
    model: string;
    threshold: number;
    /** Number of scored source/translation pairs. */
    scored: number;
    averageScore: number;
    /** Pairs scored below the threshold. */
    belowThreshold: number;
    /** Pairs the judge failed to score (unparsable or failed batches). */
    unscored: number;
    /** Score histogram by decade: '0-9' ... '90-99' plus '100'. */
    distribution: Record<string, number>;
};

export type TranslateReportCounters = {
    /** `partial`: translated files with parts the engine left untranslated, also counted in `translated`. */
    files: {translated: number; failed: number; retried: number; partial: number};
    units: {
        total: number;
        translated: number;
        fromCache: number;
        untranslated: number;
        oversized: number;
    };
    chars: {source: number; translated: number; request: number};
    /** Token usage as reported by the provider; null when not reported. */
    tokens: {input: number; output: number} | null;
    requests: {total: number; fallback: number; retries: number};
    /** `hints`: units sent to the model together with their previous version from the seed. */
    cache: {enabled: boolean; hits: number; misses: number; hitRate: number | null; hints: number};
    /** Markup and translation defects handled in model output before composing. */
    fixes: {
        markupStripped: number;
        markupRetried: number;
        markupDamaged: number;
        untranslatedRetried: number;
        untranslatedKept: number;
    };
};

export type TranslateReportTarget = TranslateReportCounters & {
    language: string;
    judge?: TranslateReportJudge;
};

export type TranslateRunReport = {
    schemaVersion: number;
    /** ISO 8601 start time of the CLI process. */
    startedAt: string;
    finishedAt: string;
    durationMs: number;
    status: TranslateReportStatus;
    provider: string;
    model?: string;
    fallbackModel?: string;
    /** Code processing mode of the run, see `--code`. */
    code?: CodeMode;
    /** True when at least one request was served by the fallback model. */
    fallbackUsed: boolean;
    dryRun: boolean;
    sourceLanguage: string;
    targetLanguages: string[];
    files: {selected: number; skipped: number};
    totals: TranslateReportCounters;
    targets: TranslateReportTarget[];
    errors: TranslateReportError[];
};

/**
 * Mutable per-target counters incremented by providers at the points
 * where the events already happen (request sent, cache hit, retry,
 * fallback, file failed). Mapped into the report schema by RunReport.
 */
export type TargetStat = {
    inputTokens: number;
    outputTokens: number;
    /** True when the provider returned token usage at least once. */
    usageSeen: boolean;
    requests: number;
    /** Characters actually sent in translation requests. */
    bytes: number;
    /** Units served from the persistent cache (including seeds). */
    cached: number;
    /** Units the enabled cache did not cover. */
    cacheMisses: number;
    cacheEnabled: boolean;
    /** Units sent to the model with their previous version from the seed memory. */
    memoryHints: number;
    /** Localized code blocks and lines of existing translations put back into the output. */
    fragmentsRestored: number;
    /** Units returned by the model untranslated. */
    untranslated: number;
    /** Delimiter runs of inline markup the model added around fragments and the CLI removed. */
    markupStripped: number;
    /** Fragments re-requested because the model returned them with damaged markup. */
    markupRetried: number;
    /** Fragments that kept their source text because the retry did not fix the markup. */
    markupDamaged: number;
    /** Fragments re-requested because the model returned them untranslated. */
    untranslatedRetried: number;
    /** Fragments that kept their source text because the retry returned them untranslated again; also counted in `untranslated`. */
    untranslatedKept: number;
    fallbackRequests: number;
    /** Extra request attempts after retryable errors. */
    retries: number;
    unitsTotal: number;
    /** Units translated by the provider during this run. */
    translatedUnits: number;
    /** Units skipped as too big for a single request. */
    oversized: number;
    sourceChars: number;
    translatedChars: number;
    filesTranslated: number;
    filesFailed: number;
    filesRetried: number;
    /** Translated files with parts the engine left untranslated; also counted in `filesTranslated`. */
    filesPartial: number;
};

export function createTargetStat(): TargetStat {
    return {
        inputTokens: 0,
        outputTokens: 0,
        usageSeen: false,
        requests: 0,
        bytes: 0,
        cached: 0,
        cacheMisses: 0,
        cacheEnabled: false,
        memoryHints: 0,
        fragmentsRestored: 0,
        untranslated: 0,
        markupStripped: 0,
        markupRetried: 0,
        markupDamaged: 0,
        untranslatedRetried: 0,
        untranslatedKept: 0,
        fallbackRequests: 0,
        retries: 0,
        unitsTotal: 0,
        translatedUnits: 0,
        oversized: 0,
        sourceChars: 0,
        translatedChars: 0,
        filesTranslated: 0,
        filesFailed: 0,
        filesRetried: 0,
        filesPartial: 0,
    };
}

/** Builds the full score histogram: '0-9' ... '90-99' plus exact '100'. */
export function scoreDistribution(scores: number[]): Record<string, number> {
    const distribution: Record<string, number> = {};

    for (let low = 0; low < 100; low += 10) {
        distribution[`${low}-${low + 9}`] = 0;
    }
    distribution['100'] = 0;

    for (const score of scores) {
        const bucket =
            score >= 100
                ? '100'
                : `${Math.floor(score / 10) * 10}-${Math.floor(score / 10) * 10 + 9}`;
        distribution[bucket] += 1;
    }

    return distribution;
}

function round(value: number, digits: number): number {
    const factor = Math.pow(10, digits);
    return Math.round(value * factor) / factor;
}

function targetCounters(stat: TargetStat): TranslateReportCounters {
    const lookups = stat.cached + stat.cacheMisses;

    return {
        files: {
            translated: stat.filesTranslated,
            failed: stat.filesFailed,
            retried: stat.filesRetried,
            partial: stat.filesPartial,
        },
        units: {
            total: stat.unitsTotal,
            translated: stat.translatedUnits,
            fromCache: stat.cached,
            untranslated: stat.untranslated,
            oversized: stat.oversized,
        },
        chars: {
            source: stat.sourceChars,
            translated: stat.translatedChars,
            request: stat.bytes,
        },
        tokens: stat.usageSeen ? {input: stat.inputTokens, output: stat.outputTokens} : null,
        requests: {
            total: stat.requests,
            fallback: stat.fallbackRequests,
            retries: stat.retries,
        },
        cache: {
            enabled: stat.cacheEnabled,
            hits: stat.cached,
            misses: stat.cacheMisses,
            hitRate: stat.cacheEnabled && lookups > 0 ? round(stat.cached / lookups, 4) : null,
            hints: stat.memoryHints,
        },
        fixes: {
            markupStripped: stat.markupStripped,
            markupRetried: stat.markupRetried,
            markupDamaged: stat.markupDamaged,
            untranslatedRetried: stat.untranslatedRetried,
            untranslatedKept: stat.untranslatedKept,
        },
    };
}

function sumCounters(targets: TranslateReportCounters[]): TranslateReportCounters {
    const totals = targetCounters(createTargetStat());
    const tokens = {input: 0, output: 0};
    let usageSeen = false;
    let cacheEnabled = false;
    let hits = 0;
    let misses = 0;
    let hints = 0;

    for (const target of targets) {
        totals.files.translated += target.files.translated;
        totals.files.failed += target.files.failed;
        totals.files.retried += target.files.retried;
        totals.files.partial += target.files.partial;
        totals.units.total += target.units.total;
        totals.units.translated += target.units.translated;
        totals.units.fromCache += target.units.fromCache;
        totals.units.untranslated += target.units.untranslated;
        totals.units.oversized += target.units.oversized;
        totals.chars.source += target.chars.source;
        totals.chars.translated += target.chars.translated;
        totals.chars.request += target.chars.request;
        totals.requests.total += target.requests.total;
        totals.requests.fallback += target.requests.fallback;
        totals.requests.retries += target.requests.retries;
        totals.fixes.markupStripped += target.fixes.markupStripped;
        totals.fixes.markupRetried += target.fixes.markupRetried;
        totals.fixes.markupDamaged += target.fixes.markupDamaged;
        totals.fixes.untranslatedRetried += target.fixes.untranslatedRetried;
        totals.fixes.untranslatedKept += target.fixes.untranslatedKept;

        if (target.tokens) {
            usageSeen = true;
            tokens.input += target.tokens.input;
            tokens.output += target.tokens.output;
        }

        cacheEnabled = cacheEnabled || target.cache.enabled;
        hits += target.cache.hits;
        misses += target.cache.misses;
        hints += target.cache.hints;
    }

    totals.tokens = usageSeen ? tokens : null;
    totals.cache = {
        enabled: cacheEnabled,
        hits,
        misses,
        hitRate: cacheEnabled && hits + misses > 0 ? round(hits / (hits + misses), 4) : null,
        hints,
    };

    return totals;
}

type RunReportInfo = {
    provider: string;
    model?: string;
    fallbackModel?: string;
    code?: CodeMode;
    dryRun: boolean;
    sourceLanguage: string;
    targetLanguages: string[];
    /** Absolute path to write the report to; the report is not written without it. */
    path?: AbsolutePath;
};

/** The slice of the resolved translate config the report is built from. */
type RunReportConfig = {
    provider: string;
    dryRun: boolean;
    source: {language: string};
    target: {language: string}[];
    report?: AbsolutePath;
    model?: string;
    fallbackModel?: string;
    code?: CodeMode;
};

/** Builds a report error entry from a caught error. */
export function reportError(
    error: unknown,
    info: {target?: string; path?: string} = {},
): TranslateReportError {
    return {
        ...info,
        code: error instanceof TranslateError ? error.code : 'UNKNOWN',
        message: String((error as Error)?.message || error),
    };
}

/**
 * Builds a report entry for a part of a file the engine left untranslated.
 * The file is still written, so the run completes as partial.
 */
export function reportExtractWarning(
    message: string,
    info: {target?: string; path?: string} = {},
): TranslateReportError {
    return {...info, code: 'EXTRACT_WARNING', message};
}

/**
 * Collects per-run translation statistics and produces the machine-readable
 * run report. Providers feed it with per-target counters and errors; the
 * report is finalized once and optionally written to `info.path`.
 */
export class RunReport {
    /** Creates the collector for a run: info from the resolved config, file counts from the planner. */
    static start(config: RunReportConfig, selected: number, skipped: number): RunReport {
        const report = new RunReport({
            provider: config.provider,
            model: config.model,
            fallbackModel: config.fallbackModel,
            code: config.code,
            dryRun: config.dryRun,
            sourceLanguage: config.source.language,
            targetLanguages: config.target.map((target) => target.language),
            path: config.report,
        });

        report.setFiles(selected, skipped);

        return report;
    }

    private readonly info: RunReportInfo;

    private files = {selected: 0, skipped: 0};

    private targets: TranslateReportTarget[] = [];

    private errors: TranslateReportError[] = [];

    private data: TranslateRunReport | null = null;

    constructor(info: RunReportInfo) {
        this.info = info;
    }

    setFiles(selected: number, skipped: number) {
        this.files = {selected, skipped};
    }

    addError(error: TranslateReportError) {
        this.errors.push(error);
    }

    addTarget(language: string, stat: TargetStat, judge?: TranslateReportJudge) {
        this.targets.push({
            language,
            ...targetCounters(stat),
            ...(judge ? {judge} : {}),
        });
    }

    /**
     * Computes the final report. Idempotent: the first call fixes the
     * result. Status is `failed` when passed explicitly (fatal error),
     * `partial` when the run completed with recorded errors, `success`
     * otherwise.
     */
    finalize(status?: 'failed'): TranslateRunReport {
        if (this.data) {
            return this.data;
        }

        const now = Date.now();
        const durationMs = Math.round(process.uptime() * 1000);
        const totals = sumCounters(this.targets);

        this.data = {
            schemaVersion: TRANSLATE_REPORT_SCHEMA_VERSION,
            startedAt: new Date(now - durationMs).toISOString(),
            finishedAt: new Date(now).toISOString(),
            durationMs,
            status: status || (this.errors.length ? 'partial' : 'success'),
            provider: this.info.provider,
            ...(this.info.model ? {model: this.info.model} : {}),
            ...(this.info.fallbackModel ? {fallbackModel: this.info.fallbackModel} : {}),
            ...(this.info.code ? {code: this.info.code} : {}),
            fallbackUsed: totals.requests.fallback > 0,
            dryRun: this.info.dryRun,
            sourceLanguage: this.info.sourceLanguage,
            targetLanguages: this.info.targetLanguages,
            files: this.files,
            totals,
            targets: this.targets,
            errors: this.errors,
        };

        return this.data;
    }

    /** Writes the finalized report to the configured path. No-op without a path. */
    write() {
        if (!this.info.path || !this.data) {
            return;
        }

        mkdirSync(dirname(this.info.path), {recursive: true});
        writeFileSync(this.info.path, JSON.stringify(this.data, null, 2) + '\n');
    }

    /**
     * Finalizes the report, writes it when a path is configured and always
     * logs the one-line run summary. Report IO failures must not fail the
     * translation run.
     */
    close(logger: TranslateLogger, status?: 'failed') {
        this.finalize(status);
        try {
            this.write();
        } catch (error) {
            logger.warn('report', `Unable to write the run report: ${(error as Error).message}`);
        }
        logger.stat(this.summary());
    }

    /** One-line human-readable run summary for the log. */
    summary(): string {
        const data = this.finalize();
        const {totals} = data;
        const seconds = round(data.durationMs / 1000, 1);
        const cached =
            totals.cache.hitRate === null
                ? `${totals.units.fromCache} cached`
                : `${totals.units.fromCache} cached, ${round(totals.cache.hitRate * 100, 1)}% hit rate`;
        const tokens = totals.tokens
            ? `; tokens: ${totals.tokens.input} in / ${totals.tokens.output} out`
            : '';
        // Only when it happened: a defect that stays at zero does not
        // deserve a place in every run summary.
        const fixes =
            (totals.fixes.markupStripped
                ? `; added markup stripped: ${totals.fixes.markupStripped}`
                : '') +
            (totals.fixes.markupRetried
                ? `; damaged markup: ${totals.fixes.markupRetried} retried, ` +
                  `${totals.fixes.markupDamaged} kept as source`
                : '') +
            (totals.fixes.untranslatedRetried
                ? `; untranslated: ${totals.fixes.untranslatedRetried} retried, ` +
                  `${totals.fixes.untranslatedKept} kept as source`
                : '');

        return (
            `run ${data.status} in ${seconds}s; ` +
            `files: ${totals.files.translated} translated` +
            (totals.files.partial ? ` (${totals.files.partial} partial)` : '') +
            `, ${totals.files.failed} failed; ` +
            `units: ${totals.units.total} (${cached}); ` +
            `chars: ${totals.chars.source} in / ${totals.chars.translated} out` +
            tokens +
            `; requests: ${totals.requests.total}` +
            ` (${totals.requests.fallback} fallback, ${totals.requests.retries} retries)` +
            fixes +
            `; errors: ${this.errors.length}`
        );
    }
}
